import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function Privacy() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen pb-16" data-testid="privacy-screen">
      <div className="max-w-2xl mx-auto px-6 pt-8">
        <div className="flex items-center gap-3 mb-7">
          <button onClick={() => nav(-1)} className="p-2 rounded-full hover:bg-white/70" data-testid="privacy-back">
            <ArrowLeft className="w-5 h-5"/>
          </button>
          <ShieldCheck className="w-6 h-6 text-[hsl(var(--neu-orange))]"/>
          <h1 className="font-display text-3xl">Privacy & data</h1>
        </div>

        <article className="prose prose-neutral max-w-none reader text-[1.1rem]">
          <h2 className="font-display text-2xl mt-6 mb-3">What we promise</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><b>No photos. No videos. No voice.</b> NeuroEQ does not store any media of you or your child, ever.</li>
            <li><b>No child accounts.</b> Only parents log in. We never collect a child's email, phone or location.</li>
            <li><b>No ads. No selling data.</b> We will never sell your reflections, your child's profile, or your activity.</li>
            <li><b>Reflections are yours.</b> Reflections are stored encrypted-at-rest and visible only to your account.</li>
          </ul>

          <h2 className="font-display text-2xl mt-8 mb-3">What we collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your email (to sign you in).</li>
            <li>Your name and (optional) mobile number, to address you politely and send reminders.</li>
            <li>Your child's first name, age group, and interests, to personalize stories. We never need their last name.</li>
            <li>Your reflections: short text answers that only you see.</li>
            <li>Anonymous usage events (e.g., session_completed) to measure if the product helps families.</li>
          </ul>

          <h2 className="font-display text-2xl mt-8 mb-3">How AI is used</h2>
          <p>
            We use OpenAI's models only to <b>personalize the existing curriculum</b> (swap a sport, a name, a hobby)
            and to convert your reflection text into shareable social posts <i>at your explicit request</i>.
            Curriculum content is human-curated. AI never invents lessons.
          </p>

          <h2 className="font-display text-2xl mt-8 mb-3">Your rights</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><b>Access</b>: see everything we have at any time in Settings.</li>
            <li><b>Edit</b>: update your name, child's profile, hobbies, and reminders anytime.</li>
            <li><b>Delete</b>: one tap in <b>Settings → Delete account</b> permanently erases everything we hold about you, including reflections, wishes, streaks, and milestones.</li>
            <li><b>Portability</b>: write to <a className="text-[hsl(var(--neu-orange))]" href="mailto:privacy@neuroeq.app">privacy@neuroeq.app</a> for an export.</li>
          </ul>

          <h2 className="font-display text-2xl mt-8 mb-3">Children's safety</h2>
          <p>
            NeuroEQ is designed for parents to use <i>with</i> their child during a weekly 30-minute session. The product does
            not require any input from the child, does not store their name beyond the parent's choice, and never asks them
            to type, take photos, or speak into the device.
          </p>

          <h2 className="font-display text-2xl mt-8 mb-3">Terms of use</h2>
          <p>
            By signing in to NeuroEQ you agree to use the service as a parent of a child aged 4–13, to use the suggested
            activities responsibly, and to understand that NeuroEQ is a <b>family ritual</b>, not a substitute for
            professional mental-health care. If you or your child are in distress, please contact a qualified professional.
          </p>

          <p className="text-sm text-muted-foreground mt-10">
            Questions? Write to us at <a className="text-[hsl(var(--neu-orange))]" href="mailto:hello@neuroeq.app">hello@neuroeq.app</a>.
          </p>
        </article>
      </div>
    </div>
  );
}
